/**
 * Image mapping system using ES module imports for production compatibility
 * Maps JSON paths to imported asset URLs that work in both dev and production
 */

// Import all images as ES modules (WebP format for optimal performance)
import logo from "@/assets/images/logo.webp";
import heroBg from "@/assets/images/hero-bg.webp";
import serviceFirst from "@/assets/images/service-first.webp";
import serviceIcon from "@/assets/images/service-icon.webp";

// Gallery images
import gallery1 from "@/assets/images/gallery-1.webp";
import gallery2 from "@/assets/images/gallery-2.webp";
import gallery3 from "@/assets/images/gallery-3.webp";
import gallery4 from "@/assets/images/gallery-4.webp";
import gallery5 from "@/assets/images/gallery-5.webp";
import gallery6 from "@/assets/images/gallery-6.webp";
import gallery7 from "@/assets/images/gallery-7.webp";
import gallery8 from "@/assets/images/gallery-8.webp";

// Things to Do images
import chatChill from "@/assets/images/chat-chill.webp";
import peacePlenty from "@/assets/images/peace-plenty.webp";
import stockingIsland from "@/assets/images/stocking-island.webp";
import sandDollar from "@/assets/images/sand-dollar.webp";
import kevalliHouse from "@/assets/images/kevalli-house.webp";
import augustaBay from "@/assets/images/augusta-bay.webp";
import diveExuma from "@/assets/images/dive-exuma.webp";
import exumaKitesurfing from "@/assets/images/exuma-kitesurfing.webp";
import exumaWatersports from "@/assets/images/exuma-watersports.webp";
import fourCsAdventures from "@/assets/images/four-cs-adventures.webp";

/**
 * Image mapping from JSON paths to imported asset URLs
 * This ensures proper asset handling in both development and production builds
 */
const imageMap: Record<string, string> = {
  "/assets/images/logo--win.png": logo,
  "/assets/images/logo.png": logo,
  "/images/logo.png": logo,
  "/assets/images/1.jpg": heroBg,
  "/assets/images/hero-bg.jpg": heroBg,
  "/images/hero-bg.jpg": heroBg,
  "/assets/images/service-first.jpg": serviceFirst,
  "/images/service-first.jpg": serviceFirst,
  "/assets/images/service-icon.png": serviceIcon,
  "/images/service-icon.png": serviceIcon,
  
  // Gallery images
  "/uploads/gallery/1.jpg": gallery1,
  "/images/gallery-1.jpg": gallery1,
  "/assets/images/gallery-1.jpg": gallery1,
  "/uploads/gallery/2.jpg": gallery2,
  "/images/gallery-2.jpg": gallery2,
  "/assets/images/gallery-2.jpg": gallery2,
  "/uploads/gallery/3.jpg": gallery3,
  "/images/gallery-3.jpg": gallery3,
  "/assets/images/gallery-3.jpg": gallery3,
  "/uploads/gallery/4.jpg": gallery4,
  "/images/gallery-4.jpg": gallery4,
  "/assets/images/gallery-4.jpg": gallery4,
  "/uploads/gallery/5.jpg": gallery5,
  "/images/gallery-5.jpg": gallery5,
  "/assets/images/gallery-5.jpg": gallery5,
  "/uploads/gallery/6.jpg": gallery6,
  "/images/gallery-6.jpg": gallery6,
  "/assets/images/gallery-6.jpg": gallery6,
  "/uploads/gallery/7.jpg": gallery7,
  "/images/gallery-7.jpg": gallery7,
  "/assets/images/gallery-7.jpg": gallery7,
  "/uploads/gallery/8.jpg": gallery8,
  "/images/gallery-8.jpg": gallery8,
  "/assets/images/gallery-8.jpg": gallery8,
  
  // Things to Do images
  "/uploads/areainfo/3c7e59ad-f718-4982-9394-0b092377c1f1-bah060c96c7c996bd29c.png": chatChill,
  "/images/chat-chill.png": chatChill,
  "/images/chat-chill.jpg": chatChill,
  "/assets/images/chat-chill.png": chatChill,
  "/assets/images/chat-chill.jpg": chatChill,
  "/uploads/areainfo/Peace%20n%20Plenty%209r.JPG": peacePlenty,
  "/images/peace-plenty.jpg": peacePlenty,
  "/assets/images/peace-plenty.jpg": peacePlenty,
  "/uploads/areainfo/fcce533230fd7e337381cdd04889c2d2.jpg": stockingIsland,
  "/images/stocking-island.jpg": stockingIsland,
  "/assets/images/stocking-island.jpg": stockingIsland,
  "/uploads/areainfo/sandb.jpg": sandDollar,
  "/images/sand-dollar.jpg": sandDollar,
  "/assets/images/sand-dollar.jpg": sandDollar,
  "/uploads/areainfo/kava5.jpg": kevalliHouse,
  "/images/kevalli-house.jpg": kevalliHouse,
  "/assets/images/kevalli-house.jpg": kevalliHouse,
  "/uploads/areainfo/augusta.jpeg": augustaBay,
  "/images/augusta-bay.jpg": augustaBay,
  "/assets/images/augusta-bay.jpg": augustaBay,
  "/uploads/areainfo/dive%20exuma7.JPG": diveExuma,
  "/images/dive-exuma.jpg": diveExuma,
  "/assets/images/dive-exuma.jpg": diveExuma,
  "/uploads/areainfo/kite9.jpg": exumaKitesurfing,
  "/images/exuma-kitesurfing.jpg": exumaKitesurfing,
  "/assets/images/exuma-kitesurfing.jpg": exumaKitesurfing,
  "/uploads/areainfo/mjemblo.jpg": exumaWatersports,
  "/images/exuma-watersports.jpg": exumaWatersports,
  "/assets/images/exuma-watersports.jpg": exumaWatersports,
  "/uploads/areainfo/foucadv8.jpg": fourCsAdventures,
  "/images/four-cs-adventures.jpg": fourCsAdventures,
  "/assets/images/four-cs-adventures.jpg": fourCsAdventures,
};

/**
 * Resolves image paths from JSON data to actual imported asset URLs
 * @param imagePath - Path from JSON data (e.g., "/assets/images/logo.png")
 * @returns Resolved asset URL that works in both dev and production
 */
export function resolveImagePath(imagePath: string): string {
  // First try exact match in image map
  if (imageMap[imagePath]) {
    return imageMap[imagePath];
  }

  // Try normalized paths (with and without /assets prefix)
  const normalizedPath = imagePath.startsWith("/assets/")
    ? imagePath
    : `/assets${imagePath.startsWith("/") ? imagePath : "/" + imagePath}`;

  if (imageMap[normalizedPath]) {
    return imageMap[normalizedPath];
  }

  // Try legacy path format
  const legacyPath = imagePath.replace("/assets", "");
  if (imageMap[legacyPath]) {
    return imageMap[legacyPath];
  }

  // Fallback: log warning and return original path
  console.warn(`Image not found in image map: ${imagePath}. Available images:`, Object.keys(imageMap));
  return imagePath;
}

/**
 * Get all available image paths for debugging
 */
export function getAvailableImages(): string[] {
  return Object.keys(imageMap);
}

/**
 * Legacy export for compatibility
 */
export const resolveImageUrl = resolveImagePath;
